import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/products";
import {
  TrendingUp,
  Handshake,
  Layers,
  ShieldCheck,
  Globe2,
  Mountain,
  Wallet,
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  Send,
} from "lucide-react";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1753771145110-b3ddcd1b0ae2";

const OPPORTUNITIES = [
  {
    icon: Layers,
    code: "01",
    title: "Project Equity",
    range: "$250K – $5M",
    blurb:
      "Take a direct equity stake in licensed Zambian copper, cobalt or coal projects. Quarterly reporting, board observer rights for qualifying tickets.",
    bullets: [
      "Equity in operating sites",
      "Quarterly distributions",
      "Board observer (qualifying)",
    ],
  },
  {
    icon: Wallet,
    code: "02",
    title: "Project Finance",
    range: "$500K – $10M",
    blurb:
      "Secured project financing against producing inventory and offtake agreements. Fixed-term notes with collateral coverage and trustee oversight.",
    bullets: [
      "Asset-backed structure",
      "Fixed-term notes",
      "Independent trustee",
    ],
  },
  {
    icon: Handshake,
    code: "03",
    title: "Offtake & Pre-pay",
    range: "$100K – $20M",
    blurb:
      "Forward-purchase commodity tonnage at agreed discount to LME. Funds are advanced against future production and secured by inventory.",
    bullets: [
      "LME-linked pricing",
      "Inventory-secured advance",
      "Recurring offtake schedule",
    ],
  },
  {
    icon: Mountain,
    code: "04",
    title: "Joint Venture",
    range: "Strategic",
    blurb:
      "Partnership structures for institutional investors and mining majors interested in long-horizon engagement across multiple Zambian assets.",
    bullets: [
      "Multi-asset JV vehicles",
      "Operating partnership",
      "Long-horizon mandates",
    ],
  },
];

const WHY = [
  {
    icon: Globe2,
    title: "World's Copper Frontier",
    body: "Zambia sits on the African Copperbelt — one of the most copper-rich regions on Earth and a critical source for the energy-transition economy.",
  },
  {
    icon: TrendingUp,
    title: "Structural Demand",
    body: "Copper, cobalt and battery metals are central to electrification, EVs and grid build-out. Demand outlook is multi-decade and structural.",
  },
  {
    icon: ShieldCheck,
    title: "Licensed & Compliant",
    body: "Zambian-registered operator with documented chain-of-custody, ZRA / Ministry of Mines compliance and independent third-party inspection.",
  },
  {
    icon: Mountain,
    title: "On-Ground Operator",
    body: "We operate from our Kabwe yard with our own logistics, weighing and grading infrastructure.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Express Interest",
    body: "Submit a brief expression of interest. Indicate ticket size and preferred structure.",
  },
  {
    n: "02",
    title: "NDA & Data Room",
    body: "Sign mutual NDA. Access curated data room: financials, licences, geo reports, offtake history.",
  },
  {
    n: "03",
    title: "Site Visit",
    body: "Optional site visit to our Kabwe HQ and export yard. Meet the operating team.",
  },
  {
    n: "04",
    title: "Term Sheet",
    body: "Negotiated term sheet, KYC, legal review. Funds flow via regulated escrow / trustee account.",
  },
  {
    n: "05",
    title: "Close & Report",
    body: "Closing, deployment of capital, quarterly investor reports and independent audit cadence.",
  },
];

const initial = {
  name: "",
  email: "",
  company: "",
  country: "",
  phone: "",
  ticket: "",
  structure: "",
  message: "",
};

export default function Invest() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const composed =
        `[INVESTMENT INQUIRY]\n` +
        `Structure: ${form.structure || "—"}\n` +
        `Ticket: ${form.ticket || "—"}\n\n` +
        `${form.message}`;

      const payload = {
        name: form.name,
        email: form.email,
        company: form.company || undefined,
        country: form.country || undefined,
        phone: form.phone || undefined,
        message: composed,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === undefined) delete payload[k];
      });
      await api.post("/inquiries", payload);
      toast.success("Inquiry received. Our investor desk will respond shortly.");
      setSubmitted(true);
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not submit. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Validation error.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-invest">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[var(--eel-border)]">
        <img
          src={HERO_IMAGE}
          alt="Copper inventory — Zambia"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-mask" />
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <p className="label-eyebrow flex items-center gap-3">
            <span className="w-8 h-px copper-bg" /> INVEST WITH EELLEVEN
          </p>
          <h1 className="font-heading mt-5 text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter max-w-4xl leading-[0.95]">
            Own a stake in <br />
            <span className="copper-text">the Copperbelt.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-zinc-200 leading-relaxed">
            EELLEVEN MINING welcomes serious investors and strategic partners.
            Project equity, secured project finance, offtake pre-pay structures
            and long-horizon joint ventures across Zambian copper, cobalt
            and coal assets.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <a
              href="#opportunities"
              data-testid="invest-cta-opportunities"
              className="px-7 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-sm hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center gap-3"
            >
              View Opportunities <ArrowRight size={16} />
            </a>
            <a
              href="#invest-form"
              data-testid="invest-cta-form"
              className="px-7 py-4 border border-[var(--eel-copper)] text-[var(--eel-copper)] font-semibold uppercase tracking-widest text-sm hover:bg-[var(--eel-copper)] hover:text-black transition-colors inline-flex items-center gap-3"
            >
              Express Interest
            </a>
          </div>
        </div>
      </section>

      {/* HEADLINE STATS */}
      <section className="border-b border-[var(--eel-border)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-[var(--eel-border)] bg-[var(--eel-surface)]">
          {[
            ["$100K", "Minimum Ticket"],
            ["$20M", "Largest Structure"],
            ["3 Sites", "Active Operations"],
            ["Quarterly", "Investor Reports"],
          ].map(([k, v], i) => (
            <div
              key={i}
              data-testid={`invest-stat-${i}`}
              className="p-6 md:p-8"
            >
              <p className="font-heading text-3xl md:text-4xl font-black copper-text">
                {k}
              </p>
              <p className="mt-2 label-eyebrow text-zinc-400">{v}</p>
            </div>
          ))}
        </div>
      </section>

      {/* OPPORTUNITIES */}
      <section
        id="opportunities"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 scroll-mt-24"
      >
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="label-eyebrow">/ Opportunities</p>
            <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
              Four ways to <br /> invest with us.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
            Each structure is designed around investor risk profile and time
            horizon. All deals run through licensed legal counsel with
            trustee-controlled disbursement.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--eel-border)] border border-[var(--eel-border)]">
          {OPPORTUNITIES.map((o) => {
            const Icon = o.icon;
            return (
              <div
                key={o.code}
                data-testid={`invest-opp-${o.code}`}
                className="bg-[var(--eel-surface)] p-7 flex flex-col"
              >
                <div className="flex items-baseline justify-between">
                  <Icon className="copper-text" size={26} />
                  <span className="label-eyebrow text-zinc-500">{o.code}</span>
                </div>
                <h3 className="mt-6 font-heading text-xl font-bold uppercase">
                  {o.title}
                </h3>
                <p className="mt-1 text-xs uppercase tracking-widest copper-text">
                  {o.range}
                </p>
                <p className="mt-4 text-sm text-zinc-400 leading-relaxed flex-1">
                  {o.blurb}
                </p>
                <ul className="mt-5 space-y-2 border-t border-[var(--eel-border)] pt-4">
                  {o.bullets.map((b, i) => (
                    <li
                      key={i}
                      className="text-xs text-zinc-300 flex items-start gap-2"
                    >
                      <CheckCircle2
                        size={12}
                        className="copper-text mt-0.5 shrink-0"
                      />
                      {b}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY ZAMBIA */}
      <section className="border-y border-[var(--eel-border)] bg-[var(--eel-surface)]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
            <div>
              <p className="label-eyebrow">/ Investment Thesis</p>
              <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
                Why Zambia. <br /> Why now.
              </h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
              Africa&apos;s electrification story runs through Zambia&apos;s
              Copperbelt. We give institutional capital direct, compliant access
              to the assets behind the trade.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--eel-border)] border border-[var(--eel-border)]">
            {WHY.map((w, i) => {
              const Icon = w.icon;
              return (
                <div
                  key={i}
                  data-testid={`invest-why-${i}`}
                  className="bg-[var(--eel-bg)] p-7"
                >
                  <Icon className="copper-text" size={26} />
                  <h3 className="mt-6 font-heading text-lg font-bold uppercase">
                    {w.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                    {w.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="label-eyebrow">/ Process</p>
            <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
              Investor onboarding, <br /> in five steps.
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm leading-relaxed">
            From expression of interest to capital deployment — typically 30–60
            days depending on diligence depth and structure.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-[var(--eel-border)] border border-[var(--eel-border)]">
          {STEPS.map((s) => (
            <div
              key={s.n}
              data-testid={`invest-step-${s.n}`}
              className="bg-[var(--eel-surface)] p-6"
            >
              <p className="font-heading text-4xl font-black copper-text">
                {s.n}
              </p>
              <h3 className="mt-4 font-heading text-base font-bold uppercase">
                {s.title}
              </h3>
              <p className="mt-2 text-xs text-zinc-400 leading-relaxed">
                {s.body}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* INVESTOR FORM */}
      <section
        id="invest-form"
        className="border-t border-[var(--eel-border)] bg-[var(--eel-surface)] scroll-mt-24"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="label-eyebrow">/ Get Started</p>
            <h2 className="font-heading mt-3 text-3xl md:text-5xl font-black uppercase tracking-tighter">
              Express your <br /> interest.
            </h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">
              Share your preferred structure and ticket size. Our investor desk
              will respond within 2 business days with the next steps and
              relevant documentation.
            </p>

            <div className="mt-8 space-y-3 text-sm">
              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="invest-contact-email"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--eel-copper)]"
              >
                <ArrowRight size={14} className="copper-text" />
                {COMPANY.email}
              </a>
              <a
                href={`https://wa.me/${COMPANY.whatsappPrimaryDigits}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="invest-contact-wa"
                className="flex items-center gap-2 text-zinc-300 hover:text-[var(--eel-copper)]"
              >
                <MessageCircle size={14} className="text-green-400" />
                WhatsApp · {COMPANY.whatsappPrimary}
              </a>
            </div>

            <Link
              to="/about"
              data-testid="invest-about-link"
              className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-300 hover:text-white border-b border-zinc-700 hover:border-white pb-0.5"
            >
              Learn about EELLEVEN <ArrowRight size={12} />
            </Link>
          </div>

          <div className="lg:col-span-7">
            <div className="border border-[var(--eel-border)] p-6 md:p-10 bg-[var(--eel-bg)]">
              {submitted ? (
                <div className="py-12 text-center" data-testid="invest-success">
                  <CheckCircle2 size={56} className="copper-text mx-auto" />
                  <h3 className="font-heading mt-6 text-3xl font-black uppercase tracking-tight">
                    Inquiry Received
                  </h3>
                  <p className="mt-3 text-zinc-400 max-w-md mx-auto">
                    Thank you. Our investor desk will reach out within 2 business
                    days with NDA and data-room access.
                  </p>
                  <button
                    data-testid="invest-success-new"
                    onClick={() => setSubmitted(false)}
                    className="mt-8 px-6 py-3 border border-[var(--eel-border-2)] text-sm uppercase tracking-widest hover:border-white"
                  >
                    Submit Another
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={submit}
                  className="space-y-6"
                  data-testid="invest-form"
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field
                      id="name"
                      label="Full Name *"
                      value={form.name}
                      onChange={update("name")}
                      required
                      minLength={2}
                      placeholder="Jane Doe"
                    />
                    <Field
                      id="email"
                      type="email"
                      label="Email *"
                      value={form.email}
                      onChange={update("email")}
                      required
                      placeholder="jane@firm.com"
                    />
                    <Field
                      id="company"
                      label="Firm / Entity"
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Capital Partners Ltd."
                    />
                    <Field
                      id="country"
                      label="Country"
                      value={form.country}
                      onChange={update("country")}
                      placeholder="United Kingdom"
                    />
                    <Field
                      id="phone"
                      label="Phone / WhatsApp"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+44 ..."
                    />
                    <Field
                      id="ticket"
                      label="Indicative Ticket"
                      value={form.ticket}
                      onChange={update("ticket")}
                      placeholder="$500,000"
                    />
                  </div>

                  <div>
                    <label
                      className="label-eyebrow text-zinc-400 block mb-2"
                      htmlFor="structure"
                    >
                      Preferred Structure
                    </label>
                    <select
                      id="structure"
                      data-testid="invest-input-structure"
                      value={form.structure}
                      onChange={update("structure")}
                      className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 focus:outline-none"
                    >
                      <option value="">-- Select preferred structure --</option>
                      {OPPORTUNITIES.map((o) => (
                        <option key={o.code} value={o.title}>
                          {o.title} ({o.range})
                        </option>
                      ))}
                      <option value="Open / Discuss">Open / Discuss</option>
                    </select>
                  </div>

                  <div>
                    <label
                      className="label-eyebrow text-zinc-400 block mb-2"
                      htmlFor="message"
                    >
                      Message *
                    </label>
                    <textarea
                      id="message"
                      data-testid="invest-input-message"
                      required
                      minLength={5}
                      rows={6}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Briefly describe your investment thesis, time horizon and any questions for our team."
                      className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="invest-submit"
                    className="w-full md:w-auto px-8 py-4 copper-bg text-black font-semibold uppercase tracking-widest text-sm hover:bg-[var(--eel-copper-hover)] transition-colors inline-flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? "Submitting..." : (
                      <>
                        Submit Inquiry <Send size={14} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Field({ id, label, ...rest }) {
  return (
    <div>
      <label className="label-eyebrow text-zinc-400 block mb-2" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        data-testid={`invest-input-${id}`}
        {...rest}
        className="w-full bg-[var(--eel-surface)] border border-[var(--eel-border)] px-4 py-3 text-sm text-zinc-100 placeholder:text-zinc-600 focus:outline-none"
      />
    </div>
  );
}
